/**
 * AnalyseTransitIAPage — Génération d'analyse des transits via Claude API
 *
 * Streaming SSE pour affichage progressif, sauvegarde en BDD, export Word.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';
import { colors } from '../styles/tokens.js';
import { useClient } from '../contexts/ClientContext.js';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface SavedAnalyse {
  id: number;
  modules: string;
  contenu: string;
  modelVersion: string;
  createdAt: string;
}

/** Convertit du HTML en paragraphes docx (parsing simplifié) */
function htmlToDocx(html: string): Paragraph[] {
  const paragraphs: Paragraph[] = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return paragraphs;

  function processNode(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) {
        paragraphs.push(new Paragraph({ children: [new TextRun({ text })], spacing: { after: 120 } }));
      }
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    if (tag === 'h1') {
      paragraphs.push(new Paragraph({ text: el.textContent ?? '', heading: HeadingLevel.HEADING_1, spacing: { before: 480, after: 200 } }));
    } else if (tag === 'h2') {
      paragraphs.push(new Paragraph({ text: el.textContent ?? '', heading: HeadingLevel.HEADING_2, spacing: { before: 360, after: 160 } }));
    } else if (tag === 'h3') {
      paragraphs.push(new Paragraph({ text: el.textContent ?? '', heading: HeadingLevel.HEADING_3, spacing: { before: 240, after: 120 } }));
    } else if (tag === 'p' || tag === 'blockquote') {
      const runs: TextRun[] = [];
      for (const child of Array.from(el.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE) {
          runs.push(new TextRun({ text: child.textContent ?? '' }));
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const childTag = (child as Element).tagName.toLowerCase();
          if (childTag === 'strong' || childTag === 'b') {
            runs.push(new TextRun({ text: (child as Element).textContent ?? '', bold: true }));
          } else if (childTag === 'em' || childTag === 'i') {
            runs.push(new TextRun({ text: (child as Element).textContent ?? '', italics: true }));
          } else {
            runs.push(new TextRun({ text: (child as Element).textContent ?? '' }));
          }
        }
      }
      if (runs.length > 0) {
        paragraphs.push(new Paragraph({ children: runs, spacing: { after: 120 }, indent: tag === 'blockquote' ? { left: 720 } : undefined }));
      }
    } else if (tag === 'table') {
      const rows = el.querySelectorAll('tr');
      for (const row of Array.from(rows)) {
        const cells = Array.from(row.querySelectorAll('th, td'));
        const text = cells.map(c => c.textContent?.trim()).join(' | ');
        paragraphs.push(new Paragraph({ children: [new TextRun({ text, font: { name: 'Courier New' }, size: 20 })], spacing: { after: 60 } }));
      }
    } else {
      for (const child of Array.from(el.childNodes)) processNode(child);
    }
  }

  for (const child of Array.from(root.childNodes)) processNode(child);
  return paragraphs;
}

export function AnalyseTransitIAPage() {
  const { id, chartId } = useParams<{ id: string; chartId: string }>();
  const { client, selectedChartId } = useClient();

  const activeChartId = chartId ? parseInt(chartId, 10) : selectedChartId;

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedText, setGeneratedText] = useState('');
  const [savedAnalyses, setSavedAnalyses] = useState<SavedAnalyse[]>([]);
  const [activeAnalyseId, setActiveAnalyseId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [loadingExisting, setLoadingExisting] = useState(true);

  const contentRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Charger les analyses existantes
  const loadAnalyses = useCallback(() => {
    if (!id || !activeChartId) return;
    setLoadingExisting(true);
    const token = localStorage.getItem('token');

    fetch(`${API_BASE}/clients/${id}/charts/${activeChartId}/analyse-transit-ia`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setSavedAnalyses(res.data);
        }
      })
      .catch(() => { /* silencieux */ })
      .finally(() => setLoadingExisting(false));
  }, [id, activeChartId]);

  useEffect(() => { loadAnalyses(); }, [loadAnalyses]);

  // Auto-scroll pendant la génération
  useEffect(() => {
    if (isGenerating && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [generatedText, isGenerating]);

  const handleGenerate = useCallback(async () => {
    if (!id || !activeChartId) return;

    setIsGenerating(true);
    setGeneratedText('');
    setError('');
    setActiveAnalyseId(null);

    const token = localStorage.getItem('token');
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetch(
        `${API_BASE}/clients/${id}/charts/${activeChartId}/analyse-transit-ia/generate`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        const contentType = response.headers.get('content-type') ?? '';
        if (contentType.includes('application/json')) {
          const err = await response.json().catch(() => ({ error: 'Erreur serveur' }));
          throw new Error(err.error ?? `Erreur ${response.status}`);
        }
        throw new Error(`Erreur serveur (${response.status})`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Pas de stream disponible');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'text') {
              setGeneratedText((prev) => prev + data.content);
            } else if (data.type === 'done') {
              setActiveAnalyseId(data.analyseId);
              loadAnalyses();
            } else if (data.type === 'error') {
              setError(data.content);
            }
          } catch { /* ligne SSE invalide */ }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setError((err as Error).message);
      }
    } finally {
      setIsGenerating(false);
      abortRef.current = null;
    }
  }, [id, activeChartId, loadAnalyses]);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleExportWord = useCallback(async () => {
    if (!generatedText || !client) return;

    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            text: `Analyse des transits — ${client.prenom} ${client.nom}`,
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 300 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: `Générée le ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`,
                italics: true,
                color: '666666',
              }),
            ],
            spacing: { after: 400 },
          }),
          ...htmlToDocx(generatedText),
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    const filename = `Transits_${client.prenom}_${client.nom}_${new Date().toISOString().slice(0, 10)}.docx`;
    saveAs(blob, filename);
  }, [generatedText, client]);

  if (!client) return null;

  if (!activeChartId) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted, fontSize: 14 }}>
        Sélectionnez un chart de transits dans la sidebar pour générer une analyse.
      </div>
    );
  }

  return (
    <div>
      <h2 style={{
        margin: '0 0 1.5rem', fontSize: '1.25rem', fontWeight: 700,
        color: colors.text, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <i className="bi bi-stars" style={{ color: '#0891b2' }} />
        Analyse IA des transits
      </h2>

      {/* Bouton générer */}
      {!isGenerating && !generatedText && (
        <div style={{
          background: '#fff', borderRadius: 10, padding: '1.5rem',
          border: `1px solid ${colors.border}`, marginBottom: '1.5rem',
        }}>
          <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: colors.textMid, lineHeight: 1.6 }}>
            Génère une analyse approfondie de vos transits actuels basée sur les textes de cours.
            Les transits des planètes lentes (Pluton → Jupiter) sont traités en priorité.
          </p>
          <button
            onClick={handleGenerate}
            style={{
              padding: '12px 28px', borderRadius: 8,
              border: 'none', background: '#0891b2', color: '#fff',
              fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <i className="bi bi-magic" />
            Générer l'analyse des transits
          </button>

          {loadingExisting && (
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.85rem', color: colors.textMuted }}>
              Vérification d'analyses existantes...
            </p>
          )}
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
          padding: '10px 14px', fontSize: 14, color: '#991b1b',
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem',
        }}>
          <i className="bi bi-exclamation-circle" />
          {error}
        </div>
      )}

      {/* Contenu généré */}
      {(isGenerating || generatedText) && (
        <div style={{
          background: '#fff', borderRadius: 10,
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
        }}>
          {/* Barre d'outils */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', borderBottom: `1px solid ${colors.border}`,
            background: '#fafafa',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isGenerating && (
                <>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', background: '#0891b2',
                    animation: 'pulse 1.5s infinite',
                  }} />
                  <span style={{ fontSize: '0.85rem', color: '#0891b2', fontWeight: 500 }}>
                    Génération en cours...
                  </span>
                </>
              )}
              {!isGenerating && generatedText && (
                <>
                  <i className="bi bi-check-circle-fill" style={{ color: '#16a34a' }} />
                  <span style={{ fontSize: '0.85rem', color: '#16a34a', fontWeight: 500 }}>
                    Analyse terminée
                  </span>
                  {activeAnalyseId && (
                    <span style={{ fontSize: '0.8rem', color: colors.textMuted, marginLeft: 8 }}>
                      (sauvegardée automatiquement)
                    </span>
                  )}
                </>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {isGenerating && (
                <button
                  onClick={handleStop}
                  style={{
                    padding: '6px 14px', borderRadius: 6, border: `1px solid ${colors.border}`,
                    background: '#fff', color: colors.textMid, fontSize: '0.8rem',
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  <i className="bi bi-stop-circle" style={{ marginRight: 4 }} />
                  Arrêter
                </button>
              )}
              {!isGenerating && generatedText && (
                <>
                  <button
                    onClick={handleExportWord}
                    style={{
                      padding: '6px 14px', borderRadius: 6, border: '1px solid #2563eb',
                      background: '#fff', color: '#2563eb', fontSize: '0.8rem',
                      cursor: 'pointer', fontFamily: 'inherit', display: 'flex',
                      alignItems: 'center', gap: 4,
                    }}
                  >
                    <i className="bi bi-file-earmark-word" />
                    Export Word
                  </button>
                  <button
                    onClick={() => {
                      setGeneratedText('');
                      setActiveAnalyseId(null);
                    }}
                    style={{
                      padding: '6px 14px', borderRadius: 6, border: '1px solid #0891b2',
                      background: '#fff', color: '#0891b2', fontSize: '0.8rem',
                      cursor: 'pointer', fontFamily: 'inherit', display: 'flex',
                      alignItems: 'center', gap: 4,
                    }}
                  >
                    <i className="bi bi-arrow-counterclockwise" />
                    Regénérer
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Texte de l'analyse */}
          <div
            ref={contentRef}
            className="rich-content"
            style={{
              padding: '2rem', maxHeight: '70vh', overflowY: 'auto',
              lineHeight: 1.8, fontSize: '0.95rem',
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(generatedText),
            }}
          />
        </div>
      )}

      {/* Historique des analyses */}
      {!isGenerating && !generatedText && savedAnalyses.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: 10, padding: '1.5rem',
          border: `1px solid ${colors.border}`,
        }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, color: colors.text }}>
            <i className="bi bi-clock-history" style={{ marginRight: 6, color: '#0891b2' }} />
            Analyses précédentes ({savedAnalyses.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {savedAnalyses.map((analyse) => (
              <div
                key={analyse.id}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 16px', borderRadius: 8,
                  border: `1px solid ${colors.border}`, background: '#fafafa',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600,
                      background: '#ecfeff', color: '#0891b2', border: '1px solid #a5f3fc',
                    }}>
                      Analyse transits
                    </span>
                    <span style={{ fontSize: '0.8rem', color: colors.textMuted }}>
                      {new Date(analyse.createdAt).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <p style={{
                    margin: 0, fontSize: '0.8rem', color: colors.textMid,
                    lineHeight: 1.5, maxHeight: 40, overflow: 'hidden',
                  }}>
                    {analyse.contenu.replace(/<[^>]*>/g, '').slice(0, 150)}...
                  </p>
                </div>
                <button
                  onClick={() => {
                    setGeneratedText(analyse.contenu);
                    setActiveAnalyseId(analyse.id);
                  }}
                  style={{
                    padding: '6px 14px', borderRadius: 6, border: `1px solid ${colors.border}`,
                    background: '#fff', color: colors.text, fontSize: '0.8rem',
                    cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', marginLeft: '1rem',
                  }}
                >
                  <i className="bi bi-eye" style={{ marginRight: 4 }} />
                  Voir
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Styles CSS */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        .rich-content h1 { font-size: 1.5rem; font-weight: 700; margin: 2rem 0 1rem; color: #164e63; border-bottom: 2px solid #e5e7eb; padding-bottom: 0.5rem; }
        .rich-content h2 { font-size: 1.25rem; font-weight: 600; margin: 1.75rem 0 0.75rem; color: #155e75; }
        .rich-content h3 { font-size: 1.1rem; font-weight: 600; margin: 1.25rem 0 0.5rem; color: #0891b2; }
        .rich-content p { margin: 0.5rem 0; }
        .rich-content blockquote { border-left: 3px solid #0891b2; padding: 0.75rem 1rem; margin: 1rem 0; background: #ecfeff; font-style: italic; color: #164e63; border-radius: 0 6px 6px 0; }
        .rich-content table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.9rem; }
        .rich-content th { background: #f3f4f6; font-weight: 600; text-align: left; padding: 8px 12px; border: 1px solid #e5e7eb; }
        .rich-content td { padding: 8px 12px; border: 1px solid #e5e7eb; }
        .rich-content tr:nth-child(even) { background: #f9fafb; }
        .rich-content strong { color: #164e63; }
      `}</style>
    </div>
  );
}
